import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Plus, Pencil, Trash2, Loader2, X } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CollectionsPage() {
  const [collections, setCollections] = useState([]);
  const [fabrics, setFabrics] = useState([]);
  const [garmentTypes, setGarmentTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", description: "", image_url: "", garment_type: "", fabric_ids: [] });
  const [newType, setNewType] = useState("");

  const load = () => {
    setLoading(true);
    Promise.all([
      axios.get(`${API}/collections`),
      axios.get(`${API}/fabrics`),
      axios.get(`${API}/garment-types`),
    ]).then(([c, f, g]) => {
      setCollections(c.data);
      setFabrics(f.data);
      setGarmentTypes(g.data);
    }).catch(console.error).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const openAdd = () => { setEditing(null); setForm({ name: "", description: "", image_url: "", garment_type: "", fabric_ids: [] }); setDialogOpen(true); };
  const openEdit = (c) => { setEditing(c); setForm({ name: c.name, description: c.description || "", image_url: c.image_url || "", garment_type: c.garment_type || "", fabric_ids: c.fabric_ids || [] }); setDialogOpen(true); };

  const save = async () => {
    try {
      if (editing) { await axios.put(`${API}/collections/${editing.id}`, form); toast.success("Collection updated"); }
      else { await axios.post(`${API}/collections`, form); toast.success("Collection added"); }
      setDialogOpen(false); load();
    } catch { toast.error("Failed to save"); }
  };

  const addGarmentType = async () => {
    if (!newType.trim()) return;
    try {
      await axios.post(`${API}/garment-types`, { name: newType.trim() });
      toast.success("Type added");
      setNewType("");
      load();
    } catch { toast.error("Failed to add type"); }
  };

  const deleteGarmentType = async (id) => {
    try { await axios.delete(`${API}/garment-types/${id}`); toast.success("Type removed"); load(); }
    catch { toast.error("Failed to delete"); }
  };

  const toggleFabric = (fabId) => {
    setForm((prev) => ({
      ...prev,
      fabric_ids: prev.fabric_ids.includes(fabId)
        ? prev.fabric_ids.filter((id) => id !== fabId)
        : [...prev.fabric_ids, fabId],
    }));
  };

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="w-6 h-6 text-[#9F7D23] animate-spin" /></div>;

  return (
    <div className="space-y-10 animate-fade-in-up" data-testid="collections-page">
      {/* Garment Types Section */}
      <div>
        <h2 className="font-['Space_Mono'] text-3xl tracking-tight uppercase text-[#5C4D24] mb-1">Catalog</h2>
        <p className="text-sm text-[#8B7536] mb-6">Manage garment types and collections</p>
        <div className="bg-white border border-[#D6D6CF] p-6" data-testid="garment-types-section">
          <h3 className="font-['Space_Mono'] text-xs tracking-widest uppercase text-[#9F7D23] mb-4">Garment Types</h3>
          <div className="flex flex-wrap gap-2 mb-4">
            {garmentTypes.map((gt) => (
              <div key={gt.id} className="flex items-center gap-1 px-3 py-1.5 border border-[#D6D6CF] group" data-testid={`type-${gt.id}`}>
                <span className="font-['Space_Mono'] text-xs uppercase tracking-wider text-[#5C4D24]">{gt.name}</span>
                <button onClick={() => deleteGarmentType(gt.id)} className="text-[#D6D6CF] hover:text-[#9F3A3A] transition-colors ml-1" data-testid={`delete-type-${gt.id}`}><X className="w-3 h-3" /></button>
              </div>
            ))}
          </div>
          <div className="flex gap-2 max-w-xs">
            <input value={newType} onChange={(e) => setNewType(e.target.value)} placeholder="New type name" data-testid="new-type-input" className="tinkah-input text-sm flex-1" onKeyDown={(e) => e.key === "Enter" && addGarmentType()} />
            <button onClick={addGarmentType} disabled={!newType.trim()} data-testid="add-type-btn" className="px-4 py-1 bg-[#9F7D23] text-white font-['Space_Mono'] text-xs uppercase disabled:opacity-50 hover:bg-[#8A6D1F] transition-all">Add</button>
          </div>
        </div>
      </div>

      {/* Collections Section */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-['Space_Mono'] text-lg tracking-tight uppercase text-[#5C4D24]">Collections</h3>
          <button onClick={openAdd} data-testid="add-collection-btn" className="flex items-center gap-2 px-6 py-3 bg-[#9F7D23] text-white font-['Space_Mono'] text-xs tracking-widest uppercase hover:bg-[#8A6D1F] transition-all active:scale-95">
            <Plus className="w-4 h-4" /> Add Collection
          </button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="collections-grid">
          {collections.map((col) => (
            <div key={col.id} className="bg-white border border-[#D6D6CF] overflow-hidden group hover:border-[#9F7D23] transition-colors" data-testid={`collection-card-${col.id}`}>
              {col.image_url && (
                <div className="aspect-video bg-[#EBEBE6] overflow-hidden">
                  <img src={col.image_url} alt={col.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                </div>
              )}
              <div className="p-4">
                <p className="font-['Space_Mono'] text-sm uppercase tracking-wider text-[#5C4D24]">{col.name}</p>
                <p className="text-xs text-[#8B7536] mt-1">{col.description}</p>
                <div className="flex items-center gap-2 mt-2">
                  <span className="font-['Space_Mono'] text-[10px] uppercase tracking-widest px-2 py-0.5 border border-[#9F7D23] text-[#9F7D23]">{col.garment_type}</span>
                  <span className="text-[10px] text-[#8B7536]">{col.fabric_ids?.length || 0} fabrics</span>
                </div>
                <div className="flex gap-2 mt-3">
                  <button onClick={() => openEdit(col)} data-testid={`edit-col-${col.id}`} className="flex items-center gap-1 px-3 py-1.5 border border-[#D6D6CF] text-[#5C4D24] font-['Space_Mono'] text-[10px] uppercase tracking-widest hover:border-[#9F7D23] transition-colors">
                    <Pencil className="w-3 h-3" /> Edit
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Collection Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="rounded-none border-[#D6D6CF] max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-['Space_Mono'] text-lg uppercase tracking-tight text-[#5C4D24]">{editing ? "Edit Collection" : "Add Collection"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-5 pt-2">
            <div>
              <label className="font-['Space_Mono'] text-[10px] tracking-widest uppercase text-[#8B7536] block mb-2">Name *</label>
              <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="col-name-input" className="tinkah-input" placeholder="Collection name" />
            </div>
            <div>
              <label className="font-['Space_Mono'] text-[10px] tracking-widest uppercase text-[#8B7536] block mb-2">Description</label>
              <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="col-desc-input" className="tinkah-input resize-none" rows={2} placeholder="Brief description" style={{ borderBottom: "2px solid #D6D6CF" }} />
            </div>
            <div>
              <label className="font-['Space_Mono'] text-[10px] tracking-widest uppercase text-[#8B7536] block mb-2">Image URL</label>
              <input value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} data-testid="col-image-input" className="tinkah-input" placeholder="https://..." />
            </div>
            <div>
              <label className="font-['Space_Mono'] text-[10px] tracking-widest uppercase text-[#8B7536] block mb-2">Garment Type</label>
              <div className="flex flex-wrap gap-2">
                {garmentTypes.map((gt) => (
                  <button key={gt.id} onClick={() => setForm({ ...form, garment_type: gt.name })} className={`px-4 py-2 border font-['Space_Mono'] text-xs tracking-widest uppercase transition-all ${form.garment_type === gt.name ? "bg-[#9F7D23] text-white border-[#9F7D23]" : "border-[#D6D6CF] text-[#5C4D24] hover:border-[#9F7D23]"}`}>
                    {gt.name}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="font-['Space_Mono'] text-[10px] tracking-widest uppercase text-[#8B7536] block mb-3">Associated Fabrics</label>
              <div className="grid grid-cols-2 gap-2 max-h-48 overflow-y-auto">
                {fabrics.map((fab) => (
                  <label key={fab.id} className="flex items-center gap-3 p-2 border border-[#D6D6CF] hover:border-[#9F7D23] cursor-pointer transition-colors" data-testid={`col-fabric-${fab.id}`}>
                    <Checkbox checked={form.fabric_ids.includes(fab.id)} onCheckedChange={() => toggleFabric(fab.id)} className="data-[state=checked]:bg-[#9F7D23] data-[state=checked]:border-[#9F7D23]" />
                    <div>
                      <p className="text-xs text-[#5C4D24]">{fab.name}</p>
                      <p className="text-[10px] text-[#8B7536]">{fab.color}</p>
                    </div>
                  </label>
                ))}
              </div>
            </div>
            <button onClick={save} disabled={!form.name || !form.garment_type} data-testid="save-collection-btn" className="w-full py-3 bg-[#9F7D23] text-white font-['Space_Mono'] text-xs tracking-widest uppercase hover:bg-[#8A6D1F] disabled:opacity-50 transition-all active:scale-95">
              {editing ? "Update Collection" : "Add Collection"}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
